import { WebsiteType } from "app/pages/sales-intelligence/domain/leads/types/enums";
import type { FilterSearchConfigMap } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filter-search-service/types";
import advancedFilteringI18nKeys from "app/pages/sales-intelligence/modules/advanced-search-filtering/utils/i18n";
import { createSignalsFiltersSearchConfig } from "app/pages/sales-intelligence/modules/signals/configs/createSignalsFiltersSearchConfig";

import type { LeadsSearchFilterKey } from "../types/leadsSearchFilters.dto";
import leadsSearchFiltersI18nKeys from "../utils/i18n";

/**
 * Creates a configuration map that defines additional search keywords for filter discoverability.
 *
 * **Purpose**: This config enhances the filter search by providing synonyms, abbreviations,
 * and alternative terms that users might type when looking for specific filters. Filters are
 * always searchable by their primary label - this config only adds supplementary keywords.
 *
 * **Why not all filters are included**: Only filters that benefit from additional search terms
 * are listed here. Filters not in this config are still fully searchable by their translated
 * label. Adding a filter here is only necessary when users might search using terminology
 * different from the filter's display name (e.g., "ARR" for Annual Revenue, "HQ" for Headquarters).
 *
 * @note
 * **Why keywords are not on filter instances**:

 * - Filter instances define structure, validation, and behavior
 * - This config handles search discoverability concerns
 * - Allows search behavior to be adjusted without modifying filter definitions
 * - Keeps filter instances decoupled from the search implementation
 */
export const createLeadsFiltersSearchConfig = (): FilterSearchConfigMap<LeadsSearchFilterKey> => ({
    // Company filters
    employeeHeadcount: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("size"),
                advancedFilteringI18nKeys.searchKeyword("staff"),
            ],
        },
    },
    annualRevenue: {
        keywords: {
            translationKeys: [advancedFilteringI18nKeys.searchKeyword("size")],
            literals: ["ARR", "MRR"],
        },
    },
    companyHeadquarter: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("country"),
                advancedFilteringI18nKeys.searchKeyword("region"),
            ],
            literals: ["HQ"],
        },
    },
    ...createSignalsFiltersSearchConfig(),
    totalFundingAmount: {
        keywords: {
            translationKeys: [advancedFilteringI18nKeys.searchKeyword("investment")],
            literals: ["VC", "PE"],
        },
    },
    businessModels: {
        keywords: {
            literals: ["B2B", "B2C", "D2C", "B2B2C"],
        },
    },
    leadScores: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("score"),
                advancedFilteringI18nKeys.searchKeyword("leadScore"),
                advancedFilteringI18nKeys.searchKeyword("priority"),
            ],
        },
    },
    adSpendChannels: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("advertising"),
                leadsSearchFiltersI18nKeys.filter("searchShare"),
                leadsSearchFiltersI18nKeys.filter("displayAdsShare"),
                advancedFilteringI18nKeys.searchKeyword("source"),
            ],
            literals: ["ads", "PPC", "SEM"],
        },
    },
    adSpendVolume: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("advertising"),
                advancedFilteringI18nKeys.searchKeyword("budget"),
                advancedFilteringI18nKeys.searchKeyword("campaigns"),
                advancedFilteringI18nKeys.searchKeyword("mobile"),
                leadsSearchFiltersI18nKeys.filter("adSpend"),
                "si.traffic-channels.displayads.label",
                "si.traffic-channels.paidsearch.label",
            ],
            literals: ["ads", "CPM", "ROAS"],
        },
    },
    displayAdsFormat: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("advertising"),
                advancedFilteringI18nKeys.searchKeyword("banner"),
                advancedFilteringI18nKeys.searchKeyword("share"),
                advancedFilteringI18nKeys.searchKeyword("native"),
                advancedFilteringI18nKeys.searchKeyword("video"),
            ],
            literals: ["ads", "GDN", "DSP"],
        },
    },

    industryFilter: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("sector"),
                advancedFilteringI18nKeys.searchKeyword("category"),
            ],
        },
        autoExpandOnSearchMatch: true,
    },
    // Same keywords as the legacy card: only one of the two is ever rendered, so they cannot both
    // answer the same search (ADR-0013).
    industryTaxonomy: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("sector"),
                advancedFilteringI18nKeys.searchKeyword("category"),
                leadsSearchFiltersI18nKeys.industryTaxonomyName(),
            ],
        },
        autoExpandOnSearchMatch: true,
    },
    industryCodesFilter: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("codes"),
                leadsSearchFiltersI18nKeys.filter("industryCodesFilter"),
            ],
            literals: ["NAICS", "NAIC", "SIC"],
        },
        autoExpandOnSearchMatch: true,
    },
    siteTags: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("tags"),
                advancedFilteringI18nKeys.searchKeyword("solutions"),
                leadsSearchFiltersI18nKeys.siteTagsTitle(),
            ],
        },
    },

    // Website filters
    technologies: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("software"),
                advancedFilteringI18nKeys.searchKeyword("tools"),
                advancedFilteringI18nKeys.searchKeyword("platform"),
            ],
            literals: ["SaaS", "API"],
        },
    },
    websiteType: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("marketplace"),
                advancedFilteringI18nKeys.searchKeyword("advertisers"),
                advancedFilteringI18nKeys.searchKeyword("publishers"),
                advancedFilteringI18nKeys.searchKeyword("consumer"),
                advancedFilteringI18nKeys.searchKeyword("direct"),
                leadsSearchFiltersI18nKeys.websiteTypeTaxonomy(WebsiteType.Advertiser),
                leadsSearchFiltersI18nKeys.websiteTypeTaxonomy(WebsiteType.Ecommerce),
                leadsSearchFiltersI18nKeys.websiteTypeTaxonomy(WebsiteType.EcommerceDtc),
                leadsSearchFiltersI18nKeys.websiteTypeTaxonomy(WebsiteType.EcommerceRetailer),
                leadsSearchFiltersI18nKeys.websiteTypeTaxonomy(WebsiteType.Publisher),
                leadsSearchFiltersI18nKeys.websiteTypeTaxonomy(WebsiteType.Transactional),
            ],
            literals: ["D2C", "DTC", "e-commerce"],
        },
    },
    topLevelDomain: {
        keywords: {
            literals: ["TLD", "URL"],
        },
    },
    keywords: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("terms"),
                advancedFilteringI18nKeys.searchKeyword("organic"),
                advancedFilteringI18nKeys.searchKeyword("paid"),
            ],
            literals: ["SEO"],
        },
    },
    onlineRevenue: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("online"),
                advancedFilteringI18nKeys.searchKeyword("sales"),
            ],
            literals: ["ecommerce", "e-commerce"],
        },
    },
    monthlyAverageTransactions: {
        keywords: {
            translationKeys: [advancedFilteringI18nKeys.searchKeyword("orders")],
        },
    },
    conversionRate: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("checkout"),
                advancedFilteringI18nKeys.searchKeyword("purchases"),
            ],
            literals: ["ecommerce", "e-commerce", "CVR"],
        },
    },

    // Traffic & Engagement filters
    countries: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("source"),
                advancedFilteringI18nKeys.searchKeyword("visitors"),
                advancedFilteringI18nKeys.searchKeyword("geography"),
                advancedFilteringI18nKeys.searchKeyword("region"),
            ],
        },
    },
    topGeoCountries: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("geography"),
                advancedFilteringI18nKeys.searchKeyword("region"),
                advancedFilteringI18nKeys.searchKeyword("location"),
            ],
        },
    },
    incomingTraffic: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("volume"),
                leadsSearchFiltersI18nKeys.filter("monthlyVisits"),
                leadsSearchFiltersI18nKeys.filter("avgMonthlyVisits"),
                leadsSearchFiltersI18nKeys.filter("uniqueVisitors"),
                leadsSearchFiltersI18nKeys.filter("totalPageViews"),
                leadsSearchFiltersI18nKeys.filter("mobileWebVisitsShare"),
            ],
        },
        autoExpandOnSearchMatch: true,
    },
    internationalFilters: {
        keywords: {
            translationKeys: [
                leadsSearchFiltersI18nKeys.filter("internationalVisits"),
                leadsSearchFiltersI18nKeys.filter("internationalCountry"),
                leadsSearchFiltersI18nKeys.filter("internationalVisitsShare"),
                advancedFilteringI18nKeys.searchKeyword("global"),
                advancedFilteringI18nKeys.searchKeyword("visitors"),
            ],
        },
        autoExpandOnSearchMatch: true,
    },
    engagement: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("activity"),
                leadsSearchFiltersI18nKeys.filter("avgPagesPerVisit"),
                leadsSearchFiltersI18nKeys.filter("avgVisitDuration"),
                leadsSearchFiltersI18nKeys.filter("avgVisitsPerUser"),
                leadsSearchFiltersI18nKeys.filter("bounceRate"),
            ],
        },
        autoExpandOnSearchMatch: true,
    },
    marketingChannels: {
        keywords: {
            translationKeys: [
                leadsSearchFiltersI18nKeys.filter("directShare"),
                leadsSearchFiltersI18nKeys.filter("emailShare"),
                leadsSearchFiltersI18nKeys.filter("referralsShare"),
                leadsSearchFiltersI18nKeys.filter("socialShare"),
                leadsSearchFiltersI18nKeys.filter("organicSearchShare"),
                leadsSearchFiltersI18nKeys.filter("paidSearchShare"),
                leadsSearchFiltersI18nKeys.filter("displayAdsShare"),
                leadsSearchFiltersI18nKeys.filter("organicSocialShare"),
                leadsSearchFiltersI18nKeys.filter("paidSocialShare"),
                leadsSearchFiltersI18nKeys.filter("genAiShare"),
                leadsSearchFiltersI18nKeys.filter("affiliateShare"),
                leadsSearchFiltersI18nKeys.filter("searchShare"),
                advancedFilteringI18nKeys.searchKeyword("acquisition"),
                advancedFilteringI18nKeys.searchKeyword("source"),
            ],
        },
        autoExpandOnSearchMatch: true,
    },
    usdSpend: {
        keywords: {
            translationKeys: [advancedFilteringI18nKeys.searchKeyword("budget")],
            literals: ["CPC"],
        },
    },
    audienceAgeGroups: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("age"),
                advancedFilteringI18nKeys.searchKeyword("demographics"),
            ],
        },
    },
    audienceGenderSplits: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("gender"),
                advancedFilteringI18nKeys.searchKeyword("demographics"),
                leadsSearchFiltersI18nKeys.filter("female_share"),
                leadsSearchFiltersI18nKeys.filter("male_share"),
            ],
        },
    },

    companyDomains: {
        keywords: {
            translationKeys: [advancedFilteringI18nKeys.searchKeyword("website")],
            literals: ["URL"],
        },
    },

    // Include/Exclude filters
    staticListsToExclude: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("exclude"),
                advancedFilteringI18nKeys.searchKeyword("blacklist"),
                advancedFilteringI18nKeys.searchKeyword("remove"),
            ],
        },
    },
    suppressionListIds: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("suppression"),
                advancedFilteringI18nKeys.searchKeyword("blacklist"),
                advancedFilteringI18nKeys.searchKeyword("exclude"),
                advancedFilteringI18nKeys.searchKeyword("remove"),
            ],
        },
    },

    // CRM filters
    salesforce: {
        keywords: {
            translationKeys: [advancedFilteringI18nKeys.searchKeyword("integration")],
            literals: ["Salesforce", "CRM", "SFDC"],
        },
    },
    hubspot: {
        keywords: {
            translationKeys: [advancedFilteringI18nKeys.searchKeyword("integration")],
            literals: ["HubSpot", "CRM", "HS"],
        },
    },
    dynamics: {
        keywords: {
            translationKeys: [advancedFilteringI18nKeys.searchKeyword("integration")],
            literals: ["Dynamics", "Dynamics 365", "CRM", "Microsoft"],
        },
    },
    crmScope: {
        keywords: {
            translationKeys: [
                advancedFilteringI18nKeys.searchKeyword("integration"),
                advancedFilteringI18nKeys.searchKeyword("new"),
                advancedFilteringI18nKeys.searchKeyword("net-new"),
                advancedFilteringI18nKeys.searchKeyword("scope"),
            ],
            literals: ["CRM", "net-new"],
        },
    },
});
